
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bookmark, Copy, ExternalLink } from 'lucide-react';
import { ContentSuggestion } from './types';
import { getPlatformIcon, getTrendIcon } from './utils';

interface ContentSuggestionCardProps {
  suggestion: ContentSuggestion;
  onSave?: (id: string) => void;
  onUse?: (id: string) => void;
}

const ContentSuggestionCard: React.FC<ContentSuggestionCardProps> = ({
  suggestion,
  onSave,
  onUse
}) => {
  return (
    <Card className="overflow-hidden h-full flex flex-col">
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1 bg-background/50">
              {getPlatformIcon(suggestion.platform)}
              <span className="capitalize">{suggestion.platform}</span>
            </Badge>
            
            <Badge 
              variant="outline" 
              className={`flex items-center gap-1 
                ${suggestion.trend === 'viral' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' : 
                  suggestion.trend === 'rising' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 
                  'bg-blue-500/10 text-blue-500 border-blue-500/30'}`}
            >
              {getTrendIcon(suggestion.trend)}
              <span className="capitalize">{suggestion.trend}</span>
            </Badge>
            
            <Badge variant="outline" className="bg-background/50">
              {suggestion.type}
            </Badge>
          </div>
          
          {suggestion.isNew && (
            <Badge variant="neon-purple" className="text-xs uppercase">
              New
            </Badge>
          )}
        </div>
        
        <h3 className="text-lg font-semibold mb-2 line-clamp-2">{suggestion.title}</h3>
        
        <p className="text-muted-foreground text-sm mb-4 flex-grow">
          {suggestion.description}
        </p>
        
        <div className="flex flex-wrap gap-1 mb-4">
          {suggestion.hashtags.map((tag, i) => (
            <Badge key={i} variant="outline" className="bg-background/50">
              #{tag}
            </Badge>
          ))}
        </div>
        
        <div className="flex justify-between items-center mt-auto">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onSave && onSave(suggestion.id)}
          >
            <Bookmark className="h-4 w-4 mr-1" />
            Save
          </Button>
          
          <Button 
            variant="neon-purple"
            size="sm"
            onClick={() => onUse && onUse(suggestion.id)}
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Use This
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContentSuggestionCard;
